import { LeftSidebarDirectory } from "../../types";
import { calculateDirectoryHeights } from "./calculateDirectoryHeights";
import { calculateFlexGrowFromHeights } from "./calculateFlexGrowFromHeights";
import {
  DIRECTORY_HEADER_HEIGHT,
  MIN_FLEX_GROW,
  MIN_RESIZABLE_HEIGHT,
} from "./constants";
import { getResizeInfo } from "./getResizeInfo";
import { DirectoryState, getFlexGrow } from "./types";

/**
 * Normalize flex-grow values so they sum to the number of open directories.
 * This ensures directories properly fill the container.
 *
 * @param directories - Current directory states
 * @returns Normalized directory states
 */
export function normalizeFlexGrow(
  directories: Record<LeftSidebarDirectory, DirectoryState>
): Record<LeftSidebarDirectory, DirectoryState> {
  const openCount = Object.values(directories).filter(d => d.isOpen).length;

  if (openCount === 0) {
    return directories;
  }

  const currentTotal = Object.values(directories).reduce(
    (sum, d) => sum + getFlexGrow(d),
    0
  );

  // If already normalized (within tolerance), return unchanged
  if (Math.abs(currentTotal - openCount) < 0.001) {
    return directories;
  }

  const scaleFactor = openCount / currentTotal;

  const result: Record<string, DirectoryState> = {};
  Object.entries(directories).forEach(([key, state]) => {
    if (state.isOpen) {
      result[key] = {
        isOpen: true,
        flexGrow: Math.max(MIN_FLEX_GROW, state.flexGrow * scaleFactor),
      };
    } else {
      result[key] = { isOpen: false, flexGrow: 0 };
    }
  });

  return result;
}

/**
 * Calculate flex-grow values when opening a directory, ensuring all directories meet minimum height.
 *
 * @param directories - Current directory states
 * @param directoryToOpen - Which directory to open
 * @param containerHeight - Total container height
 * @returns New directory states with the directory opened and heights validated
 */
export function calculateOpenDirectory(
  directories: Record<LeftSidebarDirectory, DirectoryState>,
  directoryToOpen: LeftSidebarDirectory,
  containerHeight: number
): Record<LeftSidebarDirectory, DirectoryState> {
  // Step 1: Get current heights (before opening)
  const currentHeights = calculateDirectoryHeights(
    directories,
    containerHeight
  );

  // Step 2: Create tentative state with directory opened (using average flex-grow)
  const openDirs = Object.values(directories).filter(d => d.isOpen);
  const avgFlexGrow =
    openDirs.length > 0
      ? openDirs.reduce((sum, d) => sum + getFlexGrow(d), 0) / openDirs.length
      : 1;

  const tentativeStates: Record<string, DirectoryState> = {
    ...directories,
    [directoryToOpen]: { isOpen: true, flexGrow: avgFlexGrow },
  };

  // Step 3: Calculate what heights this would produce
  const tentativeHeights = calculateDirectoryHeights(
    tentativeStates,
    containerHeight
  );

  // Step 4: Check if all open directories meet minimum
  const directoryNames = Object.keys(directories) as LeftSidebarDirectory[];
  const hasViolation = directoryNames.some(dir => {
    const state = tentativeStates[dir];
    if (!state.isOpen) return false;
    return tentativeHeights[dir] < MIN_RESIZABLE_HEIGHT;
  });

  if (!hasViolation) {
    // All good, return tentative state
    return tentativeStates;
  }

  // Step 5: VIOLATION - Redistribute heights to meet minimums
  // Strategy: Give each open directory exactly MIN_RESIZABLE_HEIGHT, distribute remainder proportionally
  const openCount = directoryNames.filter(
    d => tentativeStates[d].isOpen
  ).length;
  const collapsedCount = directoryNames.filter(
    d => !tentativeStates[d].isOpen
  ).length;
  const availableHeight =
    containerHeight - collapsedCount * DIRECTORY_HEADER_HEIGHT;
  const totalMinHeight = openCount * MIN_RESIZABLE_HEIGHT;

  if (availableHeight < totalMinHeight) {
    // Not enough space even for minimums - give equal distribution (best effort)
    const equalHeight = availableHeight / openCount;
    const targetHeights: Record<string, number> = {};
    directoryNames.forEach(dir => {
      targetHeights[dir] = tentativeStates[dir].isOpen
        ? equalHeight
        : DIRECTORY_HEADER_HEIGHT;
    });
    return calculateFlexGrowFromHeights(
      targetHeights,
      tentativeStates,
      containerHeight
    );
  }

  // We have enough space - distribute fairly
  // Give everyone minimum, then distribute remainder based on current proportions
  const remainder = availableHeight - totalMinHeight;
  const currentOpenHeights = directoryNames
    .filter(d => directories[d].isOpen)
    .map(d => currentHeights[d]);
  const totalCurrentHeight = currentOpenHeights.reduce((sum, h) => sum + h, 0);

  const targetHeights: Record<string, number> = {};
  directoryNames.forEach(dir => {
    const state = tentativeStates[dir];
    if (!state.isOpen) {
      targetHeights[dir] = DIRECTORY_HEADER_HEIGHT;
    } else if (dir === directoryToOpen) {
      // New directory gets minimum + equal share of remainder
      const share = remainder / openCount;
      targetHeights[dir] = MIN_RESIZABLE_HEIGHT + share;
    } else {
      // Existing directories get minimum + proportional share of remainder
      const proportion = currentHeights[dir] / totalCurrentHeight;
      targetHeights[dir] = MIN_RESIZABLE_HEIGHT + remainder * proportion;
    }
  });

  // Step 6: Convert back to flex-grow
  return calculateFlexGrowFromHeights(
    targetHeights,
    tentativeStates,
    containerHeight
  );
}

interface ResizeInput {
  directories: Record<LeftSidebarDirectory, DirectoryState>;
  resizingDirectory: LeftSidebarDirectory;
  deltaPixels: number;
  containerHeight: number;
}

/**
 * MAIN PIPELINE: Calculates new flex-grow values after a resize operation.
 *
 * Pipeline:
 * 1. Convert flex-grow → pixels
 * 2. Apply delta in pixel space
 * 3. Validate & clamp bounds in pixel space
 * 4. Convert pixels → flex-grow
 * 5. Return new state
 *
 * @param input - All parameters needed for calculation
 * @returns Object with directory names as keys and DirectoryState values
 */
export function calculateNewFlexGrow(
  input: ResizeInput
): Record<LeftSidebarDirectory, DirectoryState> {
  const { directories, resizingDirectory, deltaPixels, containerHeight } =
    input;

  // Use shared resize validation logic
  const resizeInfo = getResizeInfo(resizingDirectory, directories);

  if (
    !resizeInfo.canResize ||
    !resizeInfo.effectiveDirectory ||
    !resizeInfo.previousDirectory
  ) {
    return directories;
  }

  const effectiveResizingDirectory = resizeInfo.effectiveDirectory;
  const previousDirectory = resizeInfo.previousDirectory;

  // STEP 1: Convert flex-grow → pixels
  const currentHeights = calculateDirectoryHeights(
    directories,
    containerHeight
  );

  // STEP 2: Apply delta in pixel space (use effective directory)
  // Handle is at TOP: drag UP (negative) = directory grows, drag DOWN (positive) = shrinks
  const heightChange = -deltaPixels;
  const targetHeights: Record<LeftSidebarDirectory, number> = {
    ...currentHeights,
    [effectiveResizingDirectory]:
      currentHeights[effectiveResizingDirectory] + heightChange,
    [previousDirectory]: currentHeights[previousDirectory] - heightChange,
  };

  // STEP 3: Validate & clamp bounds (use effective directory)

  const validHeights: Record<LeftSidebarDirectory, number> = {
    ...targetHeights,
  };
  // For closed directories, ensure they stay at header height
  Object.entries(directories).forEach(([key, state]) => {
    if (!state.isOpen) {
      validHeights[key as LeftSidebarDirectory] = DIRECTORY_HEADER_HEIGHT;
    }
  });
  // Check if either directory would go below the minimum
  const effectiveResizingState = directories[effectiveResizingDirectory];
  const previousState = directories[previousDirectory];
  if (
    (effectiveResizingState.isOpen &&
      validHeights[effectiveResizingDirectory] < MIN_RESIZABLE_HEIGHT) ||
    (previousState.isOpen &&
      validHeights[previousDirectory] < MIN_RESIZABLE_HEIGHT)
  ) {
    // Reject the resize entirely
    return directories;
  }

  // Check if resize was rejected (heights unchanged)
  const heightsUnchanged = Object.keys(currentHeights).every(
    key =>
      Math.abs(
        currentHeights[key as LeftSidebarDirectory] -
          validHeights[key as LeftSidebarDirectory]
      ) < 0.01
  );

  if (heightsUnchanged) {
    // Resize was rejected - return original state unchanged
    return directories;
  }

  // STEP 4: Convert pixels → flex-grow
  const newDirectories = calculateFlexGrowFromHeights(
    validHeights,
    directories,
    containerHeight
  );

  // STEP 5: Return new state
  return newDirectories;
}
